"use client"
import { useMutation } from "@tanstack/react-query"
import { ArchiveAdd } from "iconsax-reactjs"
import React, { useState } from "react"
import { GoVerified } from "react-icons/go"

import { saveAffiliateByProductId, unSaveAffiliateByProductId } from "@/api"
import { TabTag } from "@/components/shared"
import { Image, Skeleton } from "@/components/ui"
import { useShowAffiliate } from "@/modules/helpers/hooks/shared"
import { cn } from "@/modules/helpers/hooks/shared/utils"
import type { Affiliate } from "@/modules/types"

interface AffiliateItemProps {
  data?: Affiliate
}

const AffiliateItem: React.FC<AffiliateItemProps> = ({ data }) => {
  const [saved, setSaved] = useState(data?.isSaved)

  const { mutate: save } = useMutation({
    mutationKey: ["save_product" + data?.id],
    mutationFn: (id: string) => saveAffiliateByProductId(id),
    onSuccess() {
      setSaved(true)
    },
    onError(e) {
      console.log("e", e)
    }
  })
  const { mutate: unSave } = useMutation({
    mutationKey: ["un_save_product" + data?.id],
    mutationFn: (id: string) => unSaveAffiliateByProductId(id),
    onSuccess() {
      setSaved(false)
    },
    onError(e) {
      console.log("e", e)
    }
  })

  const { setId } = useShowAffiliate()

  const onSaveProduct = async () => {
    if (!data?.id) return
    if (!saved) save(data.id)
    else unSave(data.id)
  }

  return (
    <div
      onClick={() => setId(data?.id || "")}
      className="flex flex-col border-1 overflow-hidden bg-white shadow-xs border-gray-50 cursor-pointer justify-between h-full rounded-2xl ease-in duration-100"
      id={data?.id}
    >
      <div className="aspect-square overflow-hidden w-full rounded-t-md relative">
        <div className="w-full rounded-2xl aspect-square border border-gray-200 overflow-hidden relative">
          <Image
            src={data?.images?.[0] || ""}
            alt=""
            className="w-full aspect-square rounded-lg shadow-sm group-hover:scale-110 object-cover transition-all duration-200 ease-linear"
          />
          <div className="rounded-tr-[16px] rounded-bl-[10px] border-l-[0.5px] border-b-[0.5px] border-neutral-200 bg-gradient-primary absolute bottom-0  px-3 flex justify-center items-center gap-1 py-1.5 text-white">
            <GoVerified size={12} color="#fff" />
            <span className="text-xss font-light">{data?.shopName || "Nhãn hàng"}</span>
          </div>
        </div>
        <div className="flex justify-end items-center absolute top-2 left-0 right-0 px-2 w-full">
          <div
            onClick={(e) => {
              e.stopPropagation()
              onSaveProduct()
            }}
            className={cn(
              "w-7 h-7 bg-[#F1F3F6] rounded-full p-1.5 flex items-center justify-center cursor-pointer",
              {
                "bg-primary-50 border border-primary": saved
              }
            )}
          >
            <ArchiveAdd size={14} color={saved ? "#045664" : "#C8CED9"} />
          </div>
        </div>
      </div>
      <div className="flex flex-col gap-1 p-2 flex-1">
        <div className="flex gap-2">
          <TabTag label="Hoa hồng cao" className="!text-white text-xss font-light" />
          <TabTag
            label={`${data?.creatorCommissionRate}%`}
            className="!text-white text-xss font-light"
          />
        </div>
        <p className="text-sm line-clamp-2 font-medium text-black flex-1">{data?.title}</p>

        <p className="text-sm line-clamp-2 font-medium text-gradient-primary">
          Nhận {!!data?.creatorCommissionValue && `${data?.creatorCommissionValue} ₫`}/đơn
        </p>
        <p className="text-xs line-clamp-2 font-light text-black">
          Đã bán {data?.soldCount?.toLocaleString()}
        </p>
      </div>
    </div>
  )
}

const AffiliateItemSkeleton = () => {
  return (
    <div className="flex flex-col gap-2">
      <Skeleton className="w-full h-full rounded-md aspect-square shadow-sm" />

      <div className="flex flex-col gap-1 p-2">
        <Skeleton className=" w-[100px] h-[20px] rounded-[4px]" />
        <Skeleton className="w-[80px] h-[20px] rounded-[4px]" />
        <Skeleton className="w-full h-[20px]" />
        <Skeleton className="w-[150px] h-[16px]" />
        <Skeleton className="h-7 w-full rounded-md" />
      </div>
    </div>
  )
}

export { AffiliateItem, AffiliateItemSkeleton }
